/**
 * CameraBarcodeScanner — modal that opens the device camera and streams
 * frames into ZXing to decode 1D/2D barcodes.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <CameraBarcodeScanner
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onDetect={(code) => { setOpen(false); attemptMatch(code); }}
 *   />
 *
 * The modal auto-picks the back camera on phones/tablets (facingMode:
 * environment) and shows a friendly permission-denied state when the browser
 * blocks camera access. Emits a soft "beep" (Web Audio) on successful decode.
 */
import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, XCircle, RefreshCw } from "lucide-react";

function beep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close?.(); }, 120);
  } catch { /* silent — audio isn't essential */ }
}

export default function CameraBarcodeScanner({ open, onClose, onDetect }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [status, setStatus] = useState("initializing"); // initializing|scanning|denied|error|no-camera
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const lastDetectRef = useRef({ code: "", ts: 0 });

  // Cleanup helper — stops the media tracks and disposes the ZXing controls.
  const stop = () => {
    try { controlsRef.current?.stop?.(); } catch { /* ignore */ }
    controlsRef.current = null;
    const stream = videoRef.current?.srcObject;
    if (stream && stream.getTracks) {
      stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startWith = async (chosenId) => {
    setStatus("initializing");
    setError("");
    try {
      if (!readerRef.current) readerRef.current = new BrowserMultiFormatReader();
      const constraints = chosenId
        ? { video: { deviceId: { exact: chosenId } } }
        : { video: { facingMode: { ideal: "environment" } } };
      const controls = await readerRef.current.decodeFromConstraints(
        constraints,
        videoRef.current,
        (result) => {
          if (!result) return;
          const code = result.getText();
          const now = Date.now();
          // Debounce: same barcode within 1.5 s is ignored (camera sees it many times).
          if (lastDetectRef.current.code === code && (now - lastDetectRef.current.ts) < 1500) return;
          lastDetectRef.current = { code, ts: now };
          beep();
          onDetect?.(code);
        },
      );
      controlsRef.current = controls;
      setStatus("scanning");
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setStatus("denied");
        setError("Camera access blocked. Enable it in your browser's site permissions.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setStatus("no-camera");
        setError("No camera found on this device.");
      } else {
        setStatus("error");
        setError(e?.message || "Could not start the camera.");
      }
    }
  };

  useEffect(() => {
    if (!open) { stop(); return undefined; }
    startWith(null);
    // Populate the "Switch camera" dropdown once we have permission
    navigator.mediaDevices?.enumerateDevices?.().then((all) => {
      const cams = all.filter((d) => d.kind === "videoinput");
      setDevices(cams);
      if (cams[0]) setDeviceId(cams[0].deviceId);
    }).catch(() => { /* ignore */ });
    return () => stop();
  }, [open]);

  const switchCamera = () => {
    if (devices.length < 2 || !deviceId) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    setDeviceId(next.deviceId);
    stop();
    startWith(next.deviceId);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="camera-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4" /> Scan with camera
          </DialogTitle>
          <DialogDescription>
            Point the camera at the barcode. It scans automatically — no need to press anything.
          </DialogDescription>
        </DialogHeader>
        <div className="relative aspect-[4/3] bg-black rounded-md overflow-hidden ring-1 ring-white/10">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            data-testid="camera-scan-video"
          />
          {/* Green scanning reticle */}
          {status === "scanning" && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-4/5 h-1/2 border-2 border-emerald-400 rounded-md shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
          )}
          {status !== "scanning" && (
            <div className="absolute inset-0 flex items-center justify-center text-white p-6 text-center bg-black/70">
              {status === "initializing" && (<div className="text-sm">Starting camera…</div>)}
              {(status === "denied" || status === "no-camera" || status === "error") && (
                <div className="flex flex-col items-center gap-3">
                  <XCircle className="w-8 h-8 text-rose-400" />
                  <div className="text-sm">{error}</div>
                  <Button variant="outline" size="sm" className="rounded-sm gap-2 bg-transparent border-white/20 text-white hover:bg-white/10"
                    onClick={() => startWith(null)} data-testid="camera-retry">
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm gap-2"
            onClick={switchCamera}
            disabled={devices.length < 2 || status !== "scanning"}
            data-testid="camera-switch"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Switch camera
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="camera-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
